import React from 'react';
import { Modal, UnstyledButton } from '@mantine/core';
import type { ReportSessionOption } from 'shared-models/browser';
import { formatMoney } from '../../../../utils/money';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import classes from './pos-session-picker-modal.module.scss';

export type PosSessionPickerModalProps = {
  opened: boolean;
  title: string;
  sessions: ReportSessionOption[];
  onPick: (session: ReportSessionOption) => void;
  onClose: () => void;
  // Restore-Z / worker report pickers need the revenue total to tell sessions apart; other
  // callers (e.g. the ZCredit transactions session picker) show only the date/name/state card.
  showAmount?: boolean;
};

const formatSessionDate = (session: ReportSessionOption): string =>
  new Date(session.openTime).toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

// Summary: Shifts picker — lists sessions as a scrollable card list; tapping one picks it.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-reports
const PosSessionPickerModalComponent: React.FC<PosSessionPickerModalProps> = ({
  opened,
  title,
  sessions,
  onPick,
  onClose,
  showAmount = true,
}) => (
  <Modal
    opened={opened}
    onClose={onClose}
    title={<strong className={classes.modalTitle}>{title}</strong>}
    size="md"
    centered
    radius="md"
    dir="rtl"
    classNames={{ root: navyModal.root, header: classes.modalHeader }}
    closeButtonProps={{ 'data-testid': 'report-session-picker-close' } as Record<string, string>}
  >
    <div className={classes.list}>
      {sessions.map((session) => (
        <UnstyledButton
          key={session.id}
          component="button"
          type="button"
          data-testid={`report-session-picker-option-${session.id}`}
          className={classes.row}
          onClick={() => onPick(session)}
        >
          <div className={classes.rowMain}>
            <div className={classes.rowTop}>
              <span className={classes.sessionDate}>{formatSessionDate(session)}</span>
              {showAmount && (
                <span className={classes.total}>
                  <span className={classes.totalValue}>{formatMoney(session.totalIncome)} ₪</span>
                  <span className={classes.totalLabel}>סה"כ הכנסות</span>
                </span>
              )}
            </div>
            <div className={classes.rowBottom}>
              <span className={classes.sessionName}>{session.name}</span>
              <span className={session.state === 'open' ? classes.sessionBadgeOpen : classes.sessionBadgeClosed}>
                {session.state === 'open' ? 'פתוח' : 'סגור'}
              </span>
            </div>
          </div>
        </UnstyledButton>
      ))}
    </div>
  </Modal>
);

export const PosSessionPickerModal = React.memo(PosSessionPickerModalComponent);
